import {
  EOrderRefundReason,
  EOrderTransactionType,
  ESellerTransactionsType,
  IOrderTransaction,
  ITransaction
} from 'models/transaction'
import { getNormalizedPrice } from 'shared/utils/getNormalizedPrice'
import { roundToKopecks } from 'shared/utils/roundToKopecks'
import { getTransactionTypeName } from './getTransactionTypeName'

export interface IOrderLedgerRow {
  key: string
  title: string
  sum: number
  comment: string
  createdAt: Date | string
  // Есть только у движения денег в ЮKassa. Начисление и списание у продавца —
  // расчёт площадки от оплаты, своего платежа у них нет
  payment_id?: string
  orderNumber?: number
}

export interface IOrderLedgerSource {
  orderNumber?: number
  orderTransactions: IOrderTransaction[]
  sellerTransactions: ITransaction[]
}

export interface IOrderLedgerSuperOrder {
  number: number
  // Сумма платежа: он один на весь супер-заказ
  sum: number
  payment_id: string | null
  ordersCount: number
}

interface ISortableRow extends IOrderLedgerRow {
  time: number
  isSeller: boolean
  isPayment: boolean
}

// Подтверждение заказа одним запросом пишет начисление продавцу и следом возвращает
// покупателю разницу. Начисление уже посчитано от остатка, поэтому в ленте оно идёт
// после этого возврата. Окно отсекает возврат разницы от более позднего повторного подтверждения
const CONFIRMATION_WINDOW_MS = 60 * 1000

const refundReasonNames: Record<EOrderRefundReason, string> = {
  [EOrderRefundReason.ITEM_CANCEL]: 'Отмена товара',
  [EOrderRefundReason.ORDER_CANCEL]: 'Отмена заказа',
  [EOrderRefundReason.ORDER_RETURN]: 'Возврат заказа',
  [EOrderRefundReason.CONFIRM_DIFFERENCE]: 'Разница при подтверждении',
  [EOrderRefundReason.MANUAL]: 'Ручной возврат'
}

const refundStatusNotes: Record<string, string> = {
  canceled: ' — не прошёл',
  pending: ' — в обработке',
  // Возврат по заказу, оплаченному по счёту: деньги переводит бухгалтерия
  manual: ' — вручную переводом'
}

const sellerTitles: Record<string, string> = {
  [ESellerTransactionsType.SALE]: 'Начисление продавцу',
  [ESellerTransactionsType.REFUND]: 'Списание у продавца'
}

const getTime = (date: Date | string) => new Date(date).getTime()

const unbreakable = (text: string) => text.replace(/ /g, '\u00A0')

const fromOrderTransaction = (
  transaction: IOrderTransaction,
  orderNumber?: number
): ISortableRow => {
  const isPayment = transaction.type === EOrderTransactionType.PAYMENT
  const reason = transaction.reason ? refundReasonNames[transaction.reason] : ''

  return {
    key: `order-${transaction._id}`,
    title: isPayment
      ? 'Оплата покупателя'
      : `Возврат покупателю${refundStatusNotes[transaction.status ?? ''] ?? ''}`,
    sum: transaction.sum,
    comment: [reason, transaction.comment].filter(Boolean).join(': '),
    createdAt: transaction.createdAt,
    payment_id: transaction.payment_id,
    orderNumber,
    time: getTime(transaction.createdAt),
    isSeller: false,
    isPayment
  }
}

const getSaleComment = (transaction: ITransaction) =>
  [
    transaction.commission
      ? `Заказ принят продавцом, комиссия ${unbreakable(getNormalizedPrice(transaction.commission))}`
      : 'Заказ принят продавцом',
    transaction.comment
  ]
    .filter(Boolean)
    .join('. ')

const getSaleTime = (
  transaction: ITransaction,
  orderTransactions: IOrderTransaction[]
) => {
  const createdAt = getTime(transaction.createdAt)
  const differenceRefunds = orderTransactions
    .filter(({ reason }) => reason === EOrderRefundReason.CONFIRM_DIFFERENCE)
    .map((refund) => getTime(refund.createdAt))
    .filter(
      (time) => time >= createdAt && time - createdAt <= CONFIRMATION_WINDOW_MS
    )

  return Math.max(createdAt, ...differenceRefunds)
}

const fromSellerTransaction = (
  transaction: ITransaction,
  { orderNumber, orderTransactions }: IOrderLedgerSource
): ISortableRow => {
  const isSale = transaction.type === ESellerTransactionsType.SALE

  return {
    key: `seller-${transaction._id}`,
    title: sellerTitles[transaction.type] ?? getTransactionTypeName(transaction.type),
    // У продажи sum уменьшается возвратами вплоть до нуля — в истории нужна сумма начисления
    sum: isSale ? transaction.credited_sum ?? transaction.sum : transaction.sum,
    comment: isSale ? getSaleComment(transaction) : transaction.comment ?? '',
    createdAt: transaction.createdAt,
    orderNumber,
    time: isSale
      ? getSaleTime(transaction, orderTransactions)
      : getTime(transaction.createdAt),
    isSeller: true,
    isPayment: false
  }
}

const getRows = (source: IOrderLedgerSource): ISortableRow[] => [
  ...source.orderTransactions.map((transaction) =>
    fromOrderTransaction(transaction, source.orderNumber)
  ),
  ...source.sellerTransactions.map((transaction) =>
    fromSellerTransaction(transaction, source)
  )
]

// Лента по времени. При равном времени деньги покупателя раньше расчётов с продавцом:
// начисление — следствие оплаты, а не наоборот
const toLedger = (rows: ISortableRow[]): IOrderLedgerRow[] =>
  [...rows]
    .sort(
      (a, b) =>
        a.time - b.time ||
        Number(a.isSeller) - Number(b.isSeller) ||
        getTime(a.createdAt) - getTime(b.createdAt)
    )
    .map(({ time, isSeller, isPayment, ...row }) => row)

export const getOrderLedger = (
  source: IOrderLedgerSource,
  superOrder?: IOrderLedgerSuperOrder | null
): IOrderLedgerRow[] =>
  toLedger(
    getRows(source).map((row) => {
      const isShare =
        !!superOrder &&
        superOrder.ordersCount > 1 &&
        row.isPayment &&
        row.payment_id === superOrder.payment_id

      return isShare
        ? {
          ...row,
          comment: `Доля заказа в платеже ${unbreakable(getNormalizedPrice(superOrder.sum))} за супер-заказ ${unbreakable(`№ ${superOrder.number}`)}`
        }
        : row
    })
  )

// Платёж один на супер-заказ, а в истории он записан долями по заказам —
// доли одного платежа собираем обратно в одну строку
export const getSuperOrderLedger = (
  sources: IOrderLedgerSource[]
): IOrderLedgerRow[] => {
  const payments = new Map<string, ISortableRow[]>()
  const rows: ISortableRow[] = []

  sources.flatMap(getRows).forEach((row) => {
    if (!row.isPayment || !row.payment_id) {
      rows.push(row)
      return
    }
    payments.set(row.payment_id, [...(payments.get(row.payment_id) ?? []), row])
  })

  payments.forEach((shares, payment_id) => {
    const first = shares.reduce((acc, share) => (share.time < acc.time ? share : acc))

    rows.push({
      ...first,
      key: `payment-${payment_id}`,
      sum: roundToKopecks(shares.reduce((acc, share) => acc + share.sum, 0)),
      comment:
        shares.length > 1
          ? shares
            .map((share) =>
              unbreakable(
                `№ ${share.orderNumber} — ${getNormalizedPrice(share.sum)}`
              )
            )
            .join(', ')
          : '',
      orderNumber: shares.length > 1 ? undefined : first.orderNumber
    })
  })

  return toLedger(rows)
}
